import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Button,
	Elem,
	ElemBefore,
	IconButton,
	SearchSelect,
	SearchSelectOptionProps,
	TextInput,
} from "@alm/flow-ds/components";
import { CommonPropertyProps, SearchColumns } from "schemas/elements";
import { useTranslation } from "react-i18next";
import { IdData } from "hooks/FormDesigner/useElementList";
import classnames from "classnames";

type Props = Omit<CommonPropertyProps, "value"> & {
	type?: string;
	value: SearchColumns[];
	nextProp?: string;
	idList: IdData;
	loading: boolean;
	disabledPlaceholder?: string;
	entityUri?: SearchSelectOptionProps;
};

export default function Columns({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	nestedField,
	idList,
	loading,
	disabledPlaceholder,
	entityUri,
	isEmu,
}: Props) {
	const { t } = useTranslation();
	const newItem = useMemo(() => {
		return { id: null, label: "" };
	}, []);

	const [requestUri, setRequestUri] = useState(entityUri);

	const [columns, setColumns] = useState<SearchColumns[]>(
		value !== undefined ? value : []
	);

	const handleChange = useCallback(
		(e, index, field: "id" | "label") => {
			const currentValues = columns.map((column, mappedIndex) => {
				if (index === mappedIndex)
					return {
						...column,
						[field]: field === "id" ? e : e.currentTarget?.value,
					};
				else return { ...column };
			});
			currentValues[index][field] =
				field === "id" ? e : e.currentTarget?.value;
			setColumns(currentValues);
		},
		[columns]
	);

	const handleBlur = useCallback(() => {
		updateStore({
			elementIndex,
			property: field,
			nestedField,
			value: columns,
		});
	}, [updateStore, elementIndex, field, nestedField, columns]);

	const handleAdd = useCallback(() => {
		setColumns([...columns, newItem]);
	}, [columns, newItem]);

	const handleRemove = useCallback(
		(index: number) => {
			const currentValues = [...columns];
			currentValues.splice(index, 1);
			setColumns(currentValues);
		},
		[columns]
	);

	useEffect(() => {
		updateStore({
			elementIndex,
			property: field,
			nestedField,
			value: columns,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columns.length]);

	// Check if Payload request uri has changed - if so remove all ID's
	useEffect(() => {
		if (!entityUri || requestUri !== entityUri) {
			const currentValues = columns.map(column => {
				return { ...column, id: null };
			});
			setColumns(currentValues);
			updateStore({
				elementIndex,
				property: field,
				nestedField,
				value: currentValues,
			});
			setRequestUri(entityUri);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [field, requestUri, entityUri]);

	return (
		<Elem className="elem-twin-input">
			<ElemBefore
				label={t("formDesigner.labels.columns")}
				required={required}
			/>
			<div className="hint-text">
				{t(`formDesigner.elements.${desc}`)}
			</div>
			<div
				className={`elem-twin-input-properties${
					validation && columns.length < 1 ? " validation" : ""
				}`}>
				{columns?.map((item: SearchColumns, index: number) => {
					return (
						<div className="elem-twin-input-item" key={index}>
							<SearchSelect
								id={`${elementIndex}-columns-${index}--id`}
								onChange={val => handleChange(val, index, "id")}
								key={`${elementIndex}-columns-${index}--id`}
								label={t("formDesigner.placeholders.columnId")}
								labelHidden={true}
								value={item.id}
								placeholder={
									!idList && disabledPlaceholder
										? disabledPlaceholder
										: t(
												"formDesigner.placeholders.columnId"
										  )
								}
								options={idList}
								disabled={!idList}
								className={classnames(
									validation && !item.id ? " invalid" : "",
									!isEmu ? "text-monospace" : ""
								)}
								clearable={true}
								onBlur={handleBlur}
								loading={loading}
								onCreate={val =>
									handleChange(
										{ value: val, label: val },
										index,
										"id"
									)
								}
							/>
							<TextInput
								id={`${elementIndex}-columns-${index}--label`}
								className={
									validation && !item.label
										? " elem--error"
										: ""
								}
								onChange={e => handleChange(e, index, "label")}
								key={index}
								label={t(
									"formDesigner.placeholders.columnLabel"
								)}
								labelHidden={true}
								value={item.label}
								placeholder={t(
									"formDesigner.placeholders.columnLabel"
								)}
								onBlur={handleBlur}
							/>
							<IconButton
								label={t("formDesigner.labels.remove")}
								icon="times"
								onClick={() => handleRemove(index)}
								className="btn-outline-danger"
							/>
						</div>
					);
				})}
				<Button
					label={t("formDesigner.labels.add")}
					onClick={handleAdd}
					className="btn-default"
				/>
			</div>
		</Elem>
	);
}
